import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  getRevenueSummary,
  getTechnicianCapacity,
  getRecurringRevenue,
  getContracts,
  getRetention,
  getLeadPerformance,
} from "../../../api/analyticsApi";
import AnalyticsMetricCard from "../AnalyticsMetricCard";
import WidgetGrid from "../../ui/WidgetGrid";
// The ONE null-aware formatter. Unknown renders "—", never 0. (metricValue.js)
import { num, toMoney, safePct } from "../metricValue";
import OwnerScorecardCard from "../scorecard/OwnerScorecardCard";
import AnalyticsBrandFilter from "../scorecard/AnalyticsBrandFilter";
import "./AnalyticsDashboard.css";

/**
 * Pest operator analytics — the single landing page for a pest tenant (t3).
 * Replaces the seven /analytics/* screens and the Operations Dashboard as the
 * landing; each tile links to its drill-down. Six tiles, fixed order, each wired
 * to a source that exists today (see handoffs/analytics_audit.md).
 *
 * LATENCY (handoffs/analytics_audit.md Part 4): three of these endpoints run
 * ~18-25s on t3 (contracts, recurring-revenue, lead-performance) — longer than
 * the api client's 20s default, so they were aborting and the tiles read "—"
 * (a timeout, not a real unknown). This page therefore:
 *   - loads each tile INDEPENDENTLY, so a fast tile (revenue ~0.5s) never waits
 *     on a slow one, and one slow/failed endpoint never blanks the others;
 *   - raises the per-tile request timeout to 45s so the slow endpoints finish;
 *   - shows a per-tile loading state until that tile's own fetch resolves;
 *   - retries each tile once on failure.
 * Backend caching of the three slow endpoints is B/G's next job.
 *
 * Every value still goes through metricValue (num/toMoney/safePct): a missing
 * value is "—", a real zero is "0"/"$0"/"0%".
 */
const TILE_TIMEOUT = 45000;

// Independent per-tile loader: own loading state, retries once on failure.
function useTileData(fetchFn) {
  const [state, setState] = useState({ data: null, loading: true });
  useEffect(() => {
    let mounted = true;
    const run = async (attempt) => {
      try {
        const res = await fetchFn();
        if (mounted) setState({ data: res?.data ?? null, loading: false });
      } catch (e) {
        if (attempt === 0) { run(1); return; } // retry exactly once
        if (mounted) setState({ data: null, loading: false });
      }
    };
    run(0);
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return state;
}

const pct0to100 = (v) => {
  const n = num(v); // util avg is already a 0..100 percent, not a 0..1 ratio
  return n === null ? "—" : `${Math.round(n)}%`;
};

export default function PestAnalyticsOverview() {
  const navigate = useNavigate();

  const revenue = useTileData(() => getRevenueSummary({}, { timeout: TILE_TIMEOUT }));
  const tech = useTileData(() => getTechnicianCapacity({}, { timeout: TILE_TIMEOUT }));
  const recurring = useTileData(() => getRecurringRevenue({}, { timeout: TILE_TIMEOUT }));
  const contracts = useTileData(() => getContracts({}, { timeout: TILE_TIMEOUT }));
  const retention = useTileData(() => getRetention({}, { timeout: TILE_TIMEOUT }));
  const lead = useTileData(() => getLeadPerformance({ days: 180 }, { timeout: TILE_TIMEOUT }));

  // Retention is off-by-default per tenant: enabled:false until the preference is
  // on. When off we point the operator to Settings — never render 0, never flip it.
  const retentionOff = retention.data && retention.data.enabled === false;
  const cancellations = useMemo(() => {
    const buckets = Array.isArray(retention.data?.buckets) ? retention.data.buckets : [];
    const row = buckets.find((b) => /cancel/i.test(b?.label || ""));
    return row ? num(row.count) : null;
  }, [retention.data]);
  const atRisk = num(retention.data?.total_at_risk_count);

  const contractCount = num(contracts.data?.active_contract_count);
  const contractValue = num(contracts.data?.recurring_contract_value);

  return (
    <div className="analytics-dashboard">
      <header className="ad-head">
        <div>
          <p className="eyebrow">Analytics</p>
          <h1>Operations &amp; Money</h1>
          <span>Collected revenue, recurring plans, crew productivity, and retention — from FieldRoutes. Tap a tile for the detail.</span>
        </div>
        <AnalyticsBrandFilter />
      </header>

      <OwnerScorecardCard />

      <WidgetGrid columns={3} className="ad-metric-grid">
        {/* 1 — Revenue (collected; FieldRoutes payments) */}
        <AnalyticsMetricCard
          title="Revenue"
          subtitle="Collected (FieldRoutes payments)"
          value={toMoney(revenue.data?.collected_revenue)}
          periodLabel="this period"
          loading={revenue.loading}
          tone="good"
          onClick={() => navigate("/analytics/revenue")}
        />

        {/* 2 — Technician productivity (FR utilization) */}
        <AnalyticsMetricCard
          title="Technician productivity"
          subtitle="Average utilization today"
          value={pct0to100(tech.data?.technician_utilization_avg)}
          periodLabel="today"
          loading={tech.loading}
          onClick={() => navigate("/analytics/technicians")}
        />

        {/* 3 — Active recurring MRR (current MRR; NOT new-in-window) */}
        <AnalyticsMetricCard
          title="Active recurring MRR"
          subtitle="Current monthly run-rate (active plans)"
          value={toMoney(recurring.data?.mrr)}
          periodLabel="current"
          loading={recurring.loading}
          onClick={() => navigate("/analytics/revenue")}
        />

        {/* 4 — Active contracts (active_contract_count; NOT won-in-window) */}
        <AnalyticsMetricCard
          title="Active contracts"
          subtitle={contractValue === null ? "Active service agreements" : `${toMoney(contractValue)} recurring value`}
          value={contractCount === null ? "—" : String(contractCount)}
          periodLabel="current"
          loading={contracts.loading}
          onClick={() => navigate("/analytics/revenue")}
        />

        {/* 5 — Cancellations & retention (off-by-default → tell them where to enable) */}
        <AnalyticsMetricCard
          title="Cancellations & retention"
          subtitle={retentionOff ? "Enable in Settings → Analytics" : `${atRisk === null ? "—" : atRisk} at risk (paused / past due)`}
          value={retentionOff ? "—" : (cancellations === null ? "—" : String(cancellations))}
          periodLabel={retentionOff ? "not enabled" : "cancellations, window"}
          loading={retention.loading}
          tone={retentionOff ? "neutral" : (cancellations ? "bad" : "neutral")}
          onClick={retentionOff ? () => navigate("/settings/analytics") : undefined}
        />

        {/* 6 — Lead conversion (0..1 ratio) */}
        <AnalyticsMetricCard
          title="Lead conversion"
          subtitle="Converted / leads"
          value={safePct(lead.data?.lead_conversion_rate)}
          periodLabel="last 180 days"
          loading={lead.loading}
          onClick={() => navigate("/analytics/lead-conversion")}
        />
      </WidgetGrid>
    </div>
  );
}
